import { StateCreator } from 'zustand';

import { DEFAULT_AUTH_PATH } from '../routeStore/constants/authPath';
import { SignOutOptions } from './types/SignOutOptions';
import { SignOutState } from './types/SignOutState';
import { SignOutStateDependencies } from './types/SignOutStateDependencies';

export const signOutStore =
  ({
    resetAllStores,
    useApiStore,
    useRouteStore,
    useUserStore,
  }: SignOutStateDependencies): StateCreator<SignOutState> =>
  (set, get) => ({
    isSigningOut: false,
    redirectingToSignOut: false,
    signOutMessage: null,

    computed: {
      get isSignOutRestricted(): boolean {
        const { pathname } = window.location;

        // The /mail and /preview flows allow users to progress through steps while holding a valid token.
        // A 401 received on these routes (ex: wrong PIN entry) must not trigger a sign-out, because doing so
        // would call resetAllStores(), wiping state like pinPurpose and leadChannelCode. That causes route guards
        // to redirect users back to /mail with an empty store, resulting in broken or incorrect API calls.
        const restrictedPaths = ['/mail', '/preview'];

        return restrictedPaths.some((path): boolean =>
          pathname.startsWith(path),
        );
      },
    },

    clearSignOutRedirect: (): void => {
      set({ redirectingToSignOut: false });
    },

    signOut: (options?: SignOutOptions): Promise<void> => {
      const {
        computed: { isSignOutRestricted },
        isSigningOut,
      } = get();

      if (isSigningOut || isSignOutRestricted) {
        return;
      }

      const { clearSession } = useUserStore.getState();
      const { setAuthRedirectPath } = useRouteStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({ isSigningOut: true, redirectingToSignOut: true });

      return portalHttpRequest
        .delete('/2.0/session', {
          suppressUnauthorizedRedirect: true,
        })
        .catch((): void => {
          // ignore for now..
        })
        .finally((): void => {
          // reset all stores to their initial state
          resetAllStores();

          clearSession();

          // set the sign out message to display on the sign out page.
          // keep redirecting to it: the router may still be rendering the page
          // the user signed out from, and that page must not render again with
          // the stores just reset (it would redirect elsewhere and the message
          // would never show). SignOutPage clears this once it renders.
          set({
            isSigningOut: false,
            redirectingToSignOut: true,
            signOutMessage: options?.withMessage,
          });

          // set the auth redirect path to where we'd want the user to go after signing back in.
          // if not provided, the user will be redirected to the default auth path after sign in.
          setAuthRedirectPath(
            options?.withAuthRedirectPath || DEFAULT_AUTH_PATH,
          );
        });
    },
  });
